import { useState } from "react";
import { Volume2, CheckCircle2, XCircle, RotateCcw } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Quiz({ story, narration }) {
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);
  const qs = story.quiz || [];
  if (!qs.length) return <p>Pas de quiz pour cette histoire.</p>;

  const q = qs[i];
  const pick = (k) => {
    if (picked !== null) return;
    setPicked(k);
    if (k === q.answer) setScore((s) => s + 1);
  };
  const next = async () => {
    if (i < qs.length - 1) { setI(i + 1); setPicked(null); return; }
    try {
      await api.post("/progress", { type: "quiz", story_id: story.id, score, total: qs.length });
      setDone(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const restart = () => { setI(0); setPicked(null); setScore(0); setDone(false); };

  if (done) return (
    <div data-testid="quiz-result" className="rise card-paper p-10 text-center">
      <p className="font-display text-6xl font-bold text-[color:var(--terracotta)]">{score} / {qs.length}</p>
      <p className="mt-3 font-display text-2xl">{score === qs.length ? "Sans faute ! Tu es un champion !" : "Bien joué ! Tu peux réessayer pour tout réussir."}</p>
      <button data-testid="quiz-restart-button" onClick={restart} className="btn-ghost mt-6"><RotateCcw size={18} /> Recommencer</button>
    </div>
  );

  return (
    <div key={i} className="rise card-paper p-6 sm:p-10">
      <p className="text-sm font-extrabold uppercase tracking-wider text-[color:var(--sage)]">Question {i + 1} sur {qs.length}</p>
      <div className="mt-2 flex items-start gap-3">
        <h2 data-testid="quiz-question" className="font-display text-3xl font-bold">{q.question}</h2>
        <button data-testid="quiz-listen-button" onClick={() => narration.play(q.question)} className="mt-1 rounded-full bg-[color:var(--paper-2)] p-2.5 hover:bg-[color:var(--sun)]"><Volume2 size={20} /></button>
      </div>
      <div className="mt-6 grid gap-3">
        {q.options.map((o, k) => {
          const good = picked !== null && k === q.answer;
          const bad = picked === k && k !== q.answer;
          return (
            <button key={k} data-testid={`quiz-option-${k}`} onClick={() => pick(k)}
              className={`flex items-center justify-between rounded-2xl border-2 px-5 py-4 text-left text-xl font-bold transition-all ${good ? "border-[color:var(--sage)] bg-[#E6F3EE]" : bad ? "border-[color:var(--terracotta)] bg-[#FCE9E5]" : "border-[color:var(--line)] bg-white hover:border-[color:var(--espresso)]"}`}>
              {o}
              {good && <CheckCircle2 className="text-[color:var(--sage)]" />}
              {bad && <XCircle className="text-[color:var(--terracotta)]" />}
            </button>
          );
        })}
      </div>
      {picked !== null && (
        <div className="mt-6 flex items-center justify-between">
          <p data-testid="quiz-feedback" className="font-display text-xl">{picked === q.answer ? "Bravo, c'est juste !" : `La bonne réponse : ${q.options[q.answer]}`}</p>
          <button data-testid="quiz-next-button" onClick={next} className="btn-primary">{i < qs.length - 1 ? "Suivante" : "Voir mon score"}</button>
        </div>
      )}
    </div>
  );
}
